/**
 * Editor state: the sequence document with undo/redo, and autosave that only
 * writes on top of the revision this client last saw (optimistic concurrency).
 * Undo (0.96.0) also covers the sequence's name, and an edit can carry a key so
 * a slider drag or typing in a field is one undo step (history.ts).
 *
 * `latest` is the source of truth for what the next save sends (0.97.0): only the edit functions and a finished
 * save write it, never a render. A render that commits an urgent update (a click) before React has applied the
 * saved revision would otherwise put the old revision back, and the next save would be refused as a conflict.
 * After a conflict nothing is saved again until the editor loads the latest revision (no retry loop).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { SequenceDocument } from '@prism/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError, errorMessage } from '../../api/client';
import { queryKeys } from '../../api/queryKeys';
import { History } from './history';
import { toast } from '../../components/Toasts';

/** What one undo step restores. */
interface Snapshot { doc: SequenceDocument; name: string }

export type SaveState = 'saved' | 'pending' | 'saving' | 'conflict' | 'error' | 'locked';

/** The autosave debounce after an edit. */
export const AUTOSAVE_DELAY_MS = 700;
/** The longest an automatic retry of a failed save waits. */
export const AUTOSAVE_MAX_DELAY_MS = 30_000;

/**
 * How long autosave waits before its next automatic try (1.0.0-rc.14): the debounce with no failures, then twice as
 * long for each save that failed in a row (1.4 s, 2.8 s, 5.6 s…), at most 30 s. Before, a failed save was retried
 * every 700 ms for ever. An edit still saves after the normal debounce.
 */
export function autosaveDelay(failures: number): number {
  if (failures <= 0) return AUTOSAVE_DELAY_MS;
  return Math.min(AUTOSAVE_MAX_DELAY_MS, AUTOSAVE_DELAY_MS * 2 ** Math.min(failures, 16));
}

export interface EditorOptions {
  /** This tab's id, sent with every save (the edit lock is this tab's, 0.97.0). */
  tabId?: string;
  /** A save was refused because someone else holds the edit lock (423 SEQUENCE_LOCKED). */
  onLocked?: (err: ApiError) => void;
}

export function useSequenceEditor(id: string, initial: { document: SequenceDocument; revision: number; name: string; description?: string }, opts: EditorOptions = {}) {
  const [doc, setDoc] = useState(initial.document);
  const [name, setName] = useState(initial.name);
  const [description, setDescription] = useState(initial.description ?? '');
  const [revision, setRevision] = useState(initial.revision);
  const [saveState, setSaveState] = useState<SaveState>('saved');
  const [saveError, setSaveError] = useState<string | null>(null);
  const history = useRef(new History<Snapshot>());
  const [, force] = useState(0);
  const dirty = useRef(false);
  const saving = useRef(false);
  const latest = useRef({ doc, name, description, revision });
  const conflict = useRef(false);
  // Edits and saves are allowed only while this tab holds the edit lock (0.97.0); the Editor sets it.
  const writable = useRef(false);
  const inFlight = useRef<Promise<void> | null>(null);
  const timer = useRef<number | null>(null);
  // Saves that failed in a row (not conflicts or the lock): the automatic retries back off (autosaveDelay).
  const failures = useRef(0);
  const qc = useQueryClient();
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const save = useCallback(async () => {
    if (saving.current || !dirty.current || conflict.current || !writable.current) return;
    let done!: () => void;
    inFlight.current = new Promise<void>((r) => { done = r; });
    saving.current = true;
    dirty.current = false;
    // While saves are failing the editor keeps saying "Not saved" until one succeeds.
    if (!failures.current) setSaveState('saving');
    const { doc: d, name: n, description: ds, revision: r } = latest.current;
    try {
      const tab = optsRef.current.tabId;
      const res = await api<{ revision: number }>(`/api/v1/sequences/${id}`, {
        method: 'PUT', headers: tab ? { 'x-prism-tab': tab } : {}, json: { baseRevision: r, document: d, name: n, description: ds },
      });
      latest.current.revision = res.revision;
      failures.current = 0;
      setRevision(res.revision);
      setSaveState(dirty.current ? 'pending' : 'saved');
      setSaveError(null);
      // The cached sequence follows the saves, so opening it again starts from this revision.
      qc.setQueryData<{ document: SequenceDocument; revision: number; name: string; description: string }>(queryKeys.sequence(id), (old) =>
        old ? { ...old, document: d, revision: res.revision, name: n, description: ds } : old);
    } catch (err) {
      const e = err as ApiError;
      dirty.current = true;
      if (e.code === 'REVISION_CONFLICT') {
        conflict.current = true;
        setSaveState('conflict');
      } else if (e.code === 'SEQUENCE_LOCKED') {
        writable.current = false;
        setSaveState('locked');
        optsRef.current.onLocked?.(e);
      } else {
        failures.current += 1;
        setSaveState('error');
        setSaveError(e.message);
        // 1.7.3: a paste of media this person cannot see (from another sequence) is refused by the save: say so once.
        if (e.code === 'SOURCE_RESTRICTED' && failures.current === 1) toast(`${e.message}. Undo the paste (⌘Z) to save again.`, 'error');
      }
    } finally {
      saving.current = false;
      inFlight.current = null;
      done();
      // The automatic retry of a failed save backs off; edits made during a save follow after the debounce.
      if (dirty.current && !conflict.current && writable.current && timer.current === null) schedule(autosaveDelay(failures.current));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, qc]);

  const schedule = useCallback((delay: number = AUTOSAVE_DELAY_MS) => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = null;
    if (conflict.current || !writable.current) return;
    timer.current = window.setTimeout(() => {
      timer.current = null;
      void save();
    }, delay);
  }, [save]);

  /**
   * Apply an edit. `coalesce`: a key for edits that belong together (one slider drag, typing in one field); a run
   * of them with the same key is one undo step. `seal()` ends such a run (pointer up, leaving the field).
   */
  const apply = useCallback((next: SequenceDocument | ((d: SequenceDocument) => SequenceDocument), coalesce?: string) => {
    if (!writable.current) return;
    const cur = latest.current.doc;
    // 1.7.3: an op that refuses (throws) is an error toast and no edit, never a crash of the editor (drags, keys).
    let n: SequenceDocument;
    try { n = typeof next === 'function' ? next(cur) : next; } catch (err) { toast(errorMessage(err), 'error'); return; }
    if (n === cur) return;
    history.current.record({ doc: cur, name: latest.current.name }, coalesce);
    latest.current.doc = n;
    dirty.current = true;
    setDoc(n);
    setSaveState((s) => (s === 'conflict' || s === 'error' ? s : 'pending'));
    schedule();
    force((x) => x + 1);
  }, [schedule]);

  const restoreSnapshot = useCallback((snap: Snapshot) => {
    if (!writable.current) return;
    latest.current.doc = snap.doc;
    latest.current.name = snap.name;
    dirty.current = true;
    setDoc(snap.doc);
    setName(snap.name);
    setSaveState((s) => (s === 'conflict' || s === 'error' ? s : 'pending'));
    schedule();
    force((x) => x + 1);
  }, [schedule]);

  const undo = useCallback(() => {
    const prev = history.current.undo({ doc: latest.current.doc, name: latest.current.name });
    if (prev) restoreSnapshot(prev);
  }, [restoreSnapshot]);

  const redo = useCallback(() => {
    const next = history.current.redo({ doc: latest.current.doc, name: latest.current.name });
    if (next) restoreSnapshot(next);
  }, [restoreSnapshot]);

  const seal = useCallback(() => history.current.seal(), []);

  /** Rename (undoable since 0.96.0; typing is one step). */
  const rename = useCallback((n: string) => {
    if (n === latest.current.name || !writable.current) return;
    history.current.record({ doc: latest.current.doc, name: latest.current.name }, 'rename');
    latest.current.name = n;
    setName(n);
    dirty.current = true;
    setSaveState((s) => (s === 'conflict' || s === 'error' ? s : 'pending'));
    schedule();
    force((x) => x + 1);
  }, [schedule]);

  const redescribe = useCallback((d: string) => {
    if (!writable.current) return;
    latest.current.description = d;
    setDescription(d);
    dirty.current = true;
    setSaveState((s) => (s === 'conflict' || s === 'error' ? s : 'pending'));
    schedule();
  }, [schedule]);

  /** Replace local state with the server's (after a conflict or a restore). */
  const reset = useCallback((d: SequenceDocument, r: number, n?: string, ds?: string) => {
    history.current.clear();
    dirty.current = false;
    conflict.current = false;
    failures.current = 0;
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = null;
    latest.current = { doc: d, revision: r, name: n || latest.current.name, description: ds ?? latest.current.description };
    setDoc(d);
    setRevision(r);
    if (n) setName(n);
    if (ds !== undefined) setDescription(ds);
    setSaveState('saved');
    setSaveError(null);
  }, []);

  /**
   * Save everything now and wait for it: the save in flight, then any edits made meanwhile. True when nothing is
   * left unsaved (before letting go of the edit lock).
   */
  const settle = useCallback(async (): Promise<boolean> => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = null;
    for (let i = 0; i < 20; i++) {
      if (inFlight.current) await inFlight.current;
      else if (dirty.current && !conflict.current && writable.current) await save();
      else break;
    }
    return !dirty.current;
  }, [save]);

  /** Allow edits (this tab holds the edit lock) or make the editor read-only. */
  const setWritable = useCallback((w: boolean) => {
    writable.current = w;
    if (!w && timer.current) { window.clearTimeout(timer.current); timer.current = null; }
    setSaveState((s) => (w ? (s === 'locked' ? (dirty.current ? 'pending' : 'saved') : s) : s));
  }, []);

  // Flush on unload so the last edits are not lost.
  useEffect(() => {
    const onUnload = (e: BeforeUnloadEvent) => {
      if (dirty.current) {
        void save();
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', onUnload);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
      if (dirty.current) void save();
    };
  }, [save]);

  return {
    doc, name, description, revision, saveState, saveError, apply, undo, redo, seal, rename, redescribe, reset, flush: save, settle, setWritable,
    /** Edits not saved yet (e.g. when the lock was lost). */
    isDirty: () => dirty.current,
    canUndo: history.current.canUndo, canRedo: history.current.canRedo,
  };
}
